import { Empty, Spin, Table, Tag, Avatar, Modal, Row, Col, Tooltip } from "antd";
import { observer } from "mobx-react";
import { useMemo, useState, useEffect } from "react";
import { useStores } from "../../../store";
import { IMPREZA_BUTTON_PROPS, __, _i } from "../../../utils";
import { AccountSubscriptionExpand, AccountSubscriptionActions, AccountAddFromEnvatoForm, ACCOUNT_SUBSCRIPTION_ACTIONS_UPGRADE_OR_DOWNGRADE } from ".";
import { MinusSquareOutlined, PlusSquareOutlined } from "@ant-design/icons";
import { ESubscriptionStatus, ESubscriptionCancellationReason } from "@devowl-wp/api-real-commerce";
import { useRealCommerceErrorHandling } from "../../../hooks";
import { RealCommerceError } from "../../../exceptions";
import { disableFomoCoupon } from "../../fomoCoupon";
import { useNavigate } from "react-router-dom";
const {
  Column
} = Table;
const AccountSubscriptionsTable = observer(() => {
  const {
    accountStore: {
      session
    }
  } = useStores();
  const {
    busyLicenses,
    busySubscriptions,
    subscriptionProductIds,
    activeSubscriptions,
    subscriptions
  } = session;
  const [showTable, setShowTable] = useState(false);
  const [isAddFromEnvatoVisible, setIsAddFromEnvatoVisible] = useState(false);
  const {
    lang
  } = document.documentElement;
  const navigate = useNavigate();
  const handleRealCommerceError = useRealCommerceErrorHandling();

  // Always expand the first non-cancelled row
  const defaultExpandedRowKeys = useMemo(() => {
    var _activeSubscriptions$;
    return [(_activeSubscriptions$ = activeSubscriptions.filter(_ref => {
      let {
        data: {
          status
        }
      } = _ref;
      return status !== ESubscriptionStatus.Canceled;
    })[0]) === null || _activeSubscriptions$ === void 0 ? void 0 : _activeSubscriptions$.data.id];
  }, [activeSubscriptions]);
  useEffect(() => {
    session.fetchSubscriptionsAndLicenses().then(() => {
      setShowTable(true);
    }).catch(function () {
      for (var _len = arguments.length, errors = new Array(_len), _key = 0; _key < _len; _key++) {
        errors[_key] = arguments[_key];
      }
      for (const error of errors) {
        if (error instanceof RealCommerceError) {
          handleRealCommerceError(error);
          return; // Only handle one error (= one modal dialog)
        }
      }
    });
  }, []);
  useEffect(() => {
    for (const productId of subscriptionProductIds) {
      disableFomoCoupon({
        reason: "user-of-product",
        productId
      });
    }
  }, [subscriptionProductIds.join(",")]);

  // When a user does not have any subscriptions or purchases, the user is definitely an affiliate -> redirect
  useEffect(() => {
    if (showTable && subscriptions.size === 0) {
      navigate("/affiliate");
    }
  }, [showTable, subscriptions]);
  if (!showTable) {
    return /*#__PURE__*/React.createElement(Spin, {
      spinning: true
    });
  }

  // Did you buy at codecanyon.net? Create an account!
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Modal, {
    open: isAddFromEnvatoVisible,
    title: __("Activate codecanyon.net purchase"),
    okText: __("Activate"),
    cancelText: __("Cancel"),
    onCancel: () => setIsAddFromEnvatoVisible(false),
    okButtonProps: {
      form: "add-from-envato",
      htmlType: "submit",
      ...IMPREZA_BUTTON_PROPS
    }
  }, /*#__PURE__*/React.createElement(AccountAddFromEnvatoForm, {
    onSave: () => setIsAddFromEnvatoVisible(false)
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      textAlign: "right",
      marginBottom: 5
    }
  }, /*#__PURE__*/React.createElement("a", {
    onClick: () => setIsAddFromEnvatoVisible(!isAddFromEnvatoVisible)
  }, __("Activate codecanyon.net purchase"))), /*#__PURE__*/React.createElement(Table, {
    bordered: true,
    locale: {
      emptyText: /*#__PURE__*/React.createElement(Empty, {
        description: __("No active product or subscriptions")
      })
    },
    pagination: false,
    loading: busyLicenses || busySubscriptions,
    dataSource: activeSubscriptions,
    rowKey: row => row.data.id,
    size: "small",
    expandable: {
      // eslint-disable-next-line react/display-name
      expandedRowRender: record => /*#__PURE__*/React.createElement(AccountSubscriptionExpand, {
        record: record
      }),
      defaultExpandedRowKeys,
      // eslint-disable-next-line react/display-name
      expandIcon: _ref2 => {
        let {
          expanded,
          onExpand,
          record
        } = _ref2;
        return /*#__PURE__*/React.createElement(Tag, {
          style: {
            cursor: "pointer"
          },
          onClick: e => onExpand(record, e),
          icon: expanded ? /*#__PURE__*/React.createElement(MinusSquareOutlined, null) : /*#__PURE__*/React.createElement(PlusSquareOutlined, null)
        }, expanded ? __("Less") : __("More"));
      },
      expandedRowClassName: () => "real-commerce-white-expandable"
    },
    showHeader: activeSubscriptions.length > 0,
    rowClassName: _ref3 => {
      let {
        data: {
          status
        }
      } = _ref3;
      return status === ESubscriptionStatus.Canceled ? "real-commerce-subscription-cancelled" : "";
    }
  }, /*#__PURE__*/React.createElement(Column, {
    title: __("Product / Subscription"),
    dataIndex: "product",
    key: "product",
    render: (_, _ref4) => {
      let {
        isOneTime,
        canBeModified,
        data: {
          status,
          productPlan: {
            name,
            product: {
              logo
            },
            description
          },
          paymentInformation,
          cancellationReason
        }
      } = _ref4;
      return /*#__PURE__*/React.createElement(Row, {
        wrap: false,
        align: "middle"
      }, !!logo && /*#__PURE__*/React.createElement(Col, {
        flex: "none"
      }, /*#__PURE__*/React.createElement(Avatar, {
        shape: "square",
        src: logo.downloadUrl,
        size: 48,
        style: {
          float: "left",
          marginRight: 15
        }
      })), /*#__PURE__*/React.createElement(Col, {
        flex: "auto"
      }, /*#__PURE__*/React.createElement("strong", null, name), /*#__PURE__*/React.createElement("br", null), description, !isOneTime && status === ESubscriptionStatus.Active && canBeModified && (paymentInformation === null || paymentInformation === void 0 ? void 0 : paymentInformation.status) !== "past_due" && /*#__PURE__*/React.createElement(React.Fragment, null, " ", "\u2022", " ", /*#__PURE__*/React.createElement("a", {
        href: "#",
        onClick: e => {
          // Should we really do this way?!?! No matter as we currently prepare for Black Friday
          // and we have planned to completely restructure the customer center with routing instead of table.
          const actionsWrapper = e.target.parentElement.parentElement.parentElement.parentElement.querySelector(".real-commerce-subscription-actions");
          actionsWrapper.dispatchEvent(new CustomEvent(ACCOUNT_SUBSCRIPTION_ACTIONS_UPGRADE_OR_DOWNGRADE));
          e.preventDefault();
        }
      }, __("Upgrade"))), (paymentInformation === null || paymentInformation === void 0 ? void 0 : paymentInformation.status) === "past_due" && /*#__PURE__*/React.createElement(Tooltip, {
        title: _i(__("{{strong}}You are behind with the payment.{{/strong}} Please update your payment method under <i>Actions > Change payment details</i> to avoid unnecessary fees!"), {
          strong: /*#__PURE__*/React.createElement("strong", null),
          i: /*#__PURE__*/React.createElement("i", null)
        })
      }, /*#__PURE__*/React.createElement(Tag, {
        style: {
          margin: "0 5px"
        },
        color: "warning"
      }, __("Past due"))), cancellationReason === ESubscriptionCancellationReason.PaymentFailed && /*#__PURE__*/React.createElement(Tooltip, {
        title: __("Your subscription was cancelled because we were unable to debit the agreed fee.")
      }, /*#__PURE__*/React.createElement(Tag, {
        style: {
          margin: "0 5px"
        },
        color: "error"
      }, __("Payment failed")))));
    }
  }), /*#__PURE__*/React.createElement(Column, {
    title: __("Next payment"),
    dataIndex: "pricing",
    key: "pricing",
    align: "right",
    width: 200,
    render: (_, _ref5) => {
      let {
        data: {
          productPlan: {
            price: [{
              currency
            }]
          },
          ecommerceSystemSubscriptionId
        },
        isOneTime,
        labelPrice,
        labelPaymentInterval
      } = _ref5;
      return isOneTime ? "-" : ecommerceSystemSubscriptionId ?
      // Recurring subscription
      __("%s %s (per %s)", currency, labelPrice, labelPaymentInterval) :
      // E.g. CodeCanyon.net activations
      __("None");
    }
  }), /*#__PURE__*/React.createElement(Column, {
    title: __("Valid until"),
    dataIndex: "validUntil",
    key: "validUntil",
    align: "right",
    width: 120,
    render: (_, _ref6) => {
      let {
        data: {
          validUntil,
          status
        },
        isLifetime,
        isInstallableProduct
      } = _ref6;
      return /*#__PURE__*/React.createElement("span", {
        style: {
          color: status === ESubscriptionStatus.Canceled ? "red" : undefined
        }
      }, !isInstallableProduct ? "-" : isLifetime ? __("Lifetime") : new Date(validUntil).toLocaleDateString(lang));
    }
  }), /*#__PURE__*/React.createElement(Column, {
    title: __("Support until"),
    dataIndex: "supportUntil",
    key: "supportUntil",
    align: "right",
    width: 120,
    render: (_, _ref7) => {
      let {
        data: {
          supportUntil
        },
        isInstallableProduct
      } = _ref7;
      return isInstallableProduct ? new Date(supportUntil).toLocaleDateString(lang) : "-";
    }
  }), /*#__PURE__*/React.createElement(Column, {
    title: __("Next invoicing"),
    dataIndex: "supportUntil",
    key: "supportUntil",
    align: "right",
    width: 120,
    render: (_, _ref8) => {
      let {
        data: {
          nextInvoicing,
          status,
          ecommerceSystemSubscriptionId
        },
        isOneTime
      } = _ref8;
      return isOneTime ? "-" : [ESubscriptionStatus.CancellationPending, ESubscriptionStatus.Canceled].indexOf(status) > -1 ? /*#__PURE__*/React.createElement(Tag, {
        color: "warning"
      }, __("Canceled")) : ecommerceSystemSubscriptionId ? new Date(nextInvoicing).toLocaleDateString(lang) : "-";
    }
  }), /*#__PURE__*/React.createElement(Column, {
    title: __("Actions"),
    dataIndex: "actions",
    key: "actions",
    align: "right",
    render: (_, record) => /*#__PURE__*/React.createElement(AccountSubscriptionActions, {
      record: record
    })
  })));
});
export { AccountSubscriptionsTable };